import {
  IModulePaymentType,
  IModuleFieldErrorData,
  ModuleBrands,
  IModuleAccessoryType,
  IAccessoryCategory,
  IAccessoryCategoryData
} from './module.types';
import {
  ModuleAccessoryTypeEnum,
  ModulePaymentTypeEnum
} from './module.constant';
import { AbstractModel } from '@/models/base';
import { IItem, Item } from '@/models/item';

export enum ModuleViewType {
  List,
  Card
}

export class Module {
  id?: number;
  name?: string;
  description?: string;
  image?: string | File;
  image_url?: string;
  is_accessories?: boolean;
  is_spare_parts?: boolean;
  is_published?: boolean;
  is_public?: boolean;
  is_subscription?: boolean;
  is_archived?: boolean;
  has_disclaimer?: boolean;
  payment_type: number = ModulePaymentTypeEnum.OneTime;
  brand?: number;
  order?: number;
  accessory_type: number = ModuleAccessoryTypeEnum.Legacy;
}

export class ModuleAccessoryType implements IModuleAccessoryType {
  readonly value: ModuleAccessoryTypeEnum;
  readonly display_name: string;

  constructor(data: IModuleAccessoryType) {
    this.value = data.value;
    this.display_name = data.display_name;
  }

  public toString(): string {
    return this.display_name;
  }

  static Legacy = new ModuleAccessoryType({
    value: ModuleAccessoryTypeEnum.Legacy,
    display_name: 'Legacy'
  });

  static Configurable = new ModuleAccessoryType({
    value: ModuleAccessoryTypeEnum.Configurable,
    display_name: 'Configurable'
  });

  static getModuleAccessoryTypeList(): IModuleAccessoryType[] {
    return [ModuleAccessoryType.Legacy, ModuleAccessoryType.Configurable];
  }
}

export class ModulePaymentType implements IModulePaymentType {
  readonly value: ModulePaymentTypeEnum;
  readonly display_name: string;

  constructor(data: IModulePaymentType) {
    this.value = data.value;
    this.display_name = data.display_name;
  }

  public toString(): string {
    return this.display_name;
  }

  static OneTime = new ModulePaymentType({
    value: ModulePaymentTypeEnum.OneTime,
    display_name: 'One Time'
  });

  static Recurring = new ModulePaymentType({
    value: ModulePaymentTypeEnum.Recurring,
    display_name: 'Recurring'
  });

  static getModulePaymentTypeList(): IModulePaymentType[] {
    return [ModulePaymentType.OneTime, ModulePaymentType.Recurring];
  }
}

export class AccessoryCategory extends AbstractModel
  implements IAccessoryCategory {
  id?: number;
  name: string;
  items: IItem[] = [];

  constructor(data: IAccessoryCategoryData) {
    super();

    const requiredFields: (keyof IAccessoryCategoryData)[] = ['name'];
    this.validate(data, requiredFields);

    this.name = data.name!;

    if (data.id) {
      this.id = data.id;
    }

    if (data.items) {
      this.items = data.items.map(item => new Item(item));
    }
  }
}

export class ModuleFieldError implements IModuleFieldErrorData {
  readonly id: string[] = [];
  readonly name: string[] = [];
  readonly description: string[] = [];
  readonly image: string[] = [];
  readonly image_url: string[] = [];
  readonly is_accessories: string[] = [];
  readonly is_spare_parts: string[] = [];
  readonly is_published: string[] = [];
  readonly is_public: string[] = [];
  readonly is_subscription: string[] = [];
  readonly is_archived: string[] = [];
  readonly has_disclaimer: string[] = [];
  readonly brand: string[] = [];
  readonly order: string[] = [];
  readonly payment_type: string[] = [];
  readonly accessory_type: string[] = [];

  constructor(data?: IModuleFieldErrorData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.description) {
        this.description = data.description;
      }

      if (data.image) {
        this.image = data.image;
      }

      if (data.image_url) {
        this.image_url = data.image_url;
      }

      if (data.is_accessories) {
        this.is_accessories = data.is_accessories;
      }

      if (data.is_spare_parts) {
        this.is_spare_parts = data.is_spare_parts;
      }

      if (data.is_published) {
        this.is_published = data.is_published;
      }

      if (data.is_public) {
        this.is_public = data.is_public;
      }

      if (data.has_disclaimer) {
        this.has_disclaimer = data.has_disclaimer;
      }

      if (data.is_subscription) {
        this.is_subscription = data.is_subscription;
      }

      if (data.is_archived) {
        this.is_archived = data.is_archived;
      }

      if (data.brand) {
        this.brand = data.brand;
      }

      if (data.order) {
        this.order = data.order;
      }

      if (data.payment_type) {
        this.payment_type = data.payment_type;
      }

      if (data.accessory_type) {
        this.accessory_type = data.accessory_type;
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.description.splice(0, this.description.length);
    this.image.splice(0, this.image.length);
    this.image_url.splice(0, this.image_url.length);
    this.is_accessories.splice(0, this.is_accessories.length);
    this.is_spare_parts.splice(0, this.is_spare_parts.length);
    this.is_published.splice(0, this.is_published.length);
    this.is_public.splice(0, this.is_public.length);
    this.is_subscription.splice(0, this.is_subscription.length);
    this.is_archived.splice(0, this.is_archived.length);
    this.has_disclaimer.splice(0, this.has_disclaimer.length);
    this.brand.splice(0, this.brand.length);
    this.order.splice(0, this.order.length);
    this.payment_type.splice(0, this.payment_type.length);
    this.accessory_type.splice(0, this.accessory_type.length);
  }

  static isModuleFieldError(data: IModuleFieldErrorData): boolean {
    if (data.name && Array.isArray(data.name)) {
      return true;
    }
    if (data.description && Array.isArray(data.description)) {
      return true;
    }
    if (data.image && Array.isArray(data.image)) {
      return true;
    }
    if (data.image_url && Array.isArray(data.image_url)) {
      return true;
    }
    if (data.is_accessories && Array.isArray(data.is_accessories)) {
      return true;
    }
    if (data.is_spare_parts && Array.isArray(data.is_spare_parts)) {
      return true;
    }
    if (data.is_published && Array.isArray(data.is_published)) {
      return true;
    }
    if (data.is_public && Array.isArray(data.is_public)) {
      return true;
    }
    if (data.has_disclaimer && Array.isArray(data.has_disclaimer)) {
      return true;
    }
    if (data.is_subscription && Array.isArray(data.is_subscription)) {
      return true;
    }
    if (data.is_archived && Array.isArray(data.is_archived)) {
      return true;
    }
    if (data.brand && Array.isArray(data.brand)) {
      return true;
    }
    if (data.order && Array.isArray(data.order)) {
      return true;
    }
    if (data.payment_type && Array.isArray(data.payment_type)) {
      return true;
    }
    if (data.accessory_type && Array.isArray(data.accessory_type)) {
      return true;
    }
    return false;
  }
}
